import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { ShowChart } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { rotationAPI } from '../../services/api';

const NS = 'dashboard.sectorRotation.outsideWatchlist';

const OutsideRotationWatchlist = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signals, setSignals] = useState([]);

  useEffect(() => {
    fetchWatchlist();
  }, []);

  const fetchWatchlist = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await rotationAPI.getOutsideWatchlist({ limit: 2 });
      const result = response.data;
      if (result.success && Array.isArray(result.signals)) {
        // One row per stock (first setup) so "Tối đa 2 mã" = 2 distinct rows
        const byCode = new Map();
        for (const s of result.signals) {
            if (!byCode.has(s.stock_code)) byCode.set(s.stock_code, s);
        }
        setSignals(Array.from(byCode.values()));
      } else {
        setSignals([]);
      }
    } catch (err) {
      console.error('Error fetching outside watchlist:', err);
      setError(err.message);
      setSignals([]);
    } finally {
      setLoading(false);
    }
  };

  const getSetupTypeLabel = (setupType) => {
    const keys = {
      breakout_hh10: `${NS}.setupBreakoutHh10`,
      pullback_ema10: `${NS}.setupPullbackEma10`,
      pullback_ema20: `${NS}.setupPullbackEma20`,
      reclaim: `${NS}.setupReclaim`,
      gap: `${NS}.setupGap`
    };
    return t(keys[setupType] || setupType);
  };

  const formatPrice = (price) => (price != null ? new Intl.NumberFormat('vi-VN').format(price) : '–');
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString(language === 'vi' ? 'vi-VN' : 'en-GB', { day: 'numeric', month: 'short' }) : '–');

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid #e2e8f0',
        bgcolor: 'white'
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ShowChart sx={{ fontSize: 16, color: '#d97706' }} />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            fontSize: '0.7rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5
          }}
        >
          {t(`${NS}.title`)}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.7rem' }}>
        {t(`${NS}.recommendation`)}
      </Typography>
      {loading && (
        <Typography variant="body2" color="text.secondary">{t(`${NS}.loading`)}</Typography>
      )}
      {error && (
        <Typography variant="body2" color="error">{error}</Typography>
      )}
      {!loading && !error && signals.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t(`${NS}.noCandidates`)}
        </Typography>
      )}
      {!loading && !error && signals.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.8, fontSize: '0.7rem', fontWeight: 700, color: '#64748b' }}>
                  {t(`${NS}.colTicker`)}
                </TableCell>
                <TableCell sx={{ py: 0.8, fontSize: '0.7rem', fontWeight: 700, color: '#64748b' }}>
                  {t(`${NS}.colSector`)}
                </TableCell>
                <TableCell sx={{ py: 0.8, fontSize: '0.7rem', fontWeight: 700, color: '#64748b' }}>
                  {t(`${NS}.colSetup`)}
                </TableCell>
                <TableCell sx={{ py: 0.8, fontSize: '0.7rem', fontWeight: 700, color: '#64748b' }}>
                  {t(`${NS}.colEntry`)}
                </TableCell>
                <TableCell sx={{ py: 0.8, fontSize: '0.7rem', fontWeight: 700, color: '#64748b' }}>
                  {t(`${NS}.colCurrent`)}
                </TableCell>
                <TableCell sx={{ py: 0.8, fontSize: '0.7rem', fontWeight: 700, color: '#64748b' }}>
                  {t(`${NS}.colTimeStop`)}
                </TableCell>
                <TableCell sx={{ py: 0.8, fontSize: '0.7rem', fontWeight: 700, color: '#64748b' }}>
                  {t(`${NS}.colFailFast`)}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signals.map((s) => {
                const pnlPct = s.current_price != null && s.entry_price != null && s.entry_price > 0
                  ? ((s.current_price - s.entry_price) / s.entry_price * 100)
                  : null;
                return (
                  <TableRow
                    key={`${s.stock_code}-${s.setup_type}`}
                    sx={{
                      bgcolor: 'rgba(251, 191, 36, 0.06)',
                      borderLeft: 3,
                      borderLeftColor: '#d97706'
                    }}
                  >
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" fontWeight="700">{s.stock_code}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {s.sector_name?.[language] || s.sector_code}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="caption" fontWeight="600">
                        {getSetupTypeLabel(s.setup_type)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" fontWeight="600">{formatPrice(s.entry_price)}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      {s.current_price != null ? (
                        <Typography variant="body2" fontWeight="600" sx={{ color: pnlPct > 0 ? 'success.main' : pnlPct < 0 ? 'error.main' : 'text.primary' }}>
                          {formatPrice(s.current_price)}
                          {pnlPct != null && (
                            <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 700 }}>
                              ({pnlPct > 0 ? '+' : ''}{pnlPct.toFixed(1)}%)
                            </Typography>
                          )}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">–</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(s.time_stop_deadline)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(s.fail_fast_deadline)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default OutsideRotationWatchlist;
